// src/pages/CreatePost.jsx
import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../hooks/useAuth';
import Cropper from 'react-easy-crop';
import getCroppedImg from '../components/common/cropImage';

const CreatePost = () => {
  const { user, getAuthenticatedApi } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [croppedImageBlob, setCroppedImageBlob] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isCropping, setIsCropping] = useState(false);
  const [tags, setTags] = useState([]);
  const [currentTag, setCurrentTag] = useState('');
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const fileInputRef = useRef(null);
  const suggestionsRef = useRef(null);

  const popularTags = [
    'vintage', 'cursive', 'historical', 'personality', 'analysis',
    'question', 'mental-health', 'progression', 'emotional',
    'professional', 'medical', 'left-handed', 'technique',
    'calligraphy', 'famous', 'research'
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        setTagSuggestions([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image size should be less than 5MB' }));
      return;
    }

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, image: 'Please upload an image file' }));
      return;
    }

    setImage(file);
    setCroppedImageBlob(null); // Reset any previous crop
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      // No longer automatically open the cropper
    };
    reader.readAsDataURL(file);
  };

  const onCropComplete = (_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  };

  const handleCropConfirm = async () => {
    if (!imagePreview || !croppedAreaPixels) return;
    try {
      const { blob, url } = await getCroppedImg(imagePreview, croppedAreaPixels);
      setCroppedImageBlob(blob);
      setImagePreview(url); // Show the cropped image preview
      setIsCropping(false);
    } catch (err) {
      console.error(err);
      setErrors(prev => ({ ...prev, image: 'Failed to crop image.' }));
    }
  };

  const handleTagInputChange = (e) => {
    const value = e.target.value;
    setCurrentTag(value);
    if (value.trim()) {
      const filtered = popularTags.filter(tag =>
        tag.toLowerCase().includes(value.toLowerCase()) &&
        !tags.includes(tag)
      );
      setTagSuggestions(filtered.slice(0, 5));
    } else {
      setTagSuggestions([]);
    }
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const trimmedTag = currentTag.trim().toLowerCase();
    if (trimmedTag && !tags.includes(trimmedTag) && tags.length < 5) {
      setTags([...tags, trimmedTag]);
      setCurrentTag('');
      setTagSuggestions([]);
    }
  };

  const selectTagSuggestion = (tag) => {
    if (tags.length < 5 && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setCurrentTag('');
      setTagSuggestions([]);
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!content.trim()) newErrors.content = 'Description is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const formData = new FormData();
    formData.append('title', title);
    formData.append('content', content);
    formData.append('tag_names', tags.join(','));

    // Use the cropped blob if it exists, otherwise use the original image file.
    const imageToUpload = croppedImageBlob || image;
    if (imageToUpload) {
      // Provide the original filename to the backend to help with MIME type detection.
      formData.append('image', imageToUpload, image.name);
    }

    try {
      const api = await getAuthenticatedApi();
      await api.post('/post/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      navigate('/forum', { state: { success: true } });
    } catch (err) {
      console.error('Post error:', err);
      const message = err.response?.data?.detail || err.message || 'Something went wrong.';
      setErrors({ submit: message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const removeImage = (e) => {
    e.stopPropagation();
    setImage(null);
    setImagePreview(null);
    setCroppedImageBlob(null);
    if(fileInputRef.current) {
      fileInputRef.current.value = null; // Reset the file input
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C0D] text-white py-8">
      <div className="max-w-4xl mx-auto px-6">
        <Link to="/forum" className="flex items-center text-gray-400 hover:text-white mb-6 transition-colors duration-200 group">
          <svg className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Forum
        </Link>

        {isCropping && imagePreview && (
          <div className="fixed inset-0 bg-black/80 z-50 flex flex-col justify-center items-center p-4">
            <div className="w-full max-w-md aspect-[4/3] relative">
              <Cropper
                image={imagePreview}
                crop={crop}
                zoom={zoom}
                aspect={4 / 3}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="mt-4 flex gap-4">
              <button
                onClick={() => setIsCropping(false)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                className="px-4 py-2 bg-primary hover:bg-blue-600 rounded"
              >
                Crop & Use Image
              </button>
            </div>
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-dark-gray/30 backdrop-blur-sm border border-gray-800 rounded-xl overflow-hidden shadow-xl"
        >
          <div className="p-8">
            <h1 className="text-2xl font-bold mb-6">Create New Post</h1>
            <form onSubmit={handleSubmit} noValidate>
              {/* Title, Description, and Tags sections remain the same... */}
              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={`w-full bg-dark-gray/50 border ${errors.title ? 'border-red-500' : 'border-gray-700'} rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary`}
                  placeholder="Enter title"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Description</label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className={`w-full bg-dark-gray/50 border ${errors.content ? 'border-red-500' : 'border-gray-700'} rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary min-h-[150px]`}
                  placeholder="Write something..."
                />
                {errors.content && <p className="text-red-500 text-sm mt-1">{errors.content}</p>}
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 mb-2">Image (Optional)</label>
                <div className={`border-2 border-dashed ${errors.image ? 'border-red-500' : 'border-gray-700'} rounded-lg p-4 text-center ${!imagePreview && 'cursor-pointer hover:border-primary'}`}
                     onClick={() => !imagePreview && fileInputRef.current.click()}>
                  {imagePreview ? (
                    <div className="relative inline-block group">
                      <img src={imagePreview} alt="Preview" className="max-h-64 mx-auto rounded-md" />
                      <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setIsCropping(true); }}
                              className="px-4 py-2 mb-2 bg-primary hover:bg-blue-600 text-white rounded-md"
                          >
                              Crop Image
                          </button>
                          <button
                              type="button"
                              onClick={removeImage}
                              className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-md"
                          >
                              Remove Image
                          </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-400">
                      <p>Click to upload image</p>
                      <p className="text-sm mt-1">JPEG, PNG (max 5MB)</p>
                    </div>
                  )}
                  <input type="file" ref={fileInputRef} onChange={handleImageChange} accept="image/*" className="hidden" />
                </div>
                {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
              </div>
              
              <div className="mb-8">
                <label className="block text-gray-300 mb-2">Tags</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {tags.map(tag => (
                    <span key={tag} className="bg-gray-800 text-gray-300 px-3 py-1 rounded-full flex items-center">
                      #{tag}
                      <button type="button" onClick={() => removeTag(tag)} className="ml-2 text-red-500 hover:text-white">✕</button>
                    </span>
                  ))}
                </div>
                <div className="relative">
                  <div className="flex">
                    <input
                      type="text"
                      value={currentTag}
                      onChange={handleTagInputChange}
                      onKeyDown={handleTagKeyDown}
                      className="flex-1 bg-dark-gray/50 border border-gray-700 rounded-l-md px-4 py-2"
                      placeholder="Type and press Enter"
                      disabled={tags.length >= 5}
                    />
                    <button
                      type="button"
                      onClick={addTag}
                      disabled={!currentTag.trim() || tags.length >= 5}
                      className="bg-gray-700 text-white px-4 py-2 rounded-r-md"
                    >
                      Add
                    </button>
                  </div>
                  {tagSuggestions.length > 0 && (
                    <div ref={suggestionsRef} className="absolute z-10 mt-1 w-full bg-gray-800 border border-gray-700 rounded-md shadow-lg max-h-60 overflow-auto">
                      {tagSuggestions.map(tag => (
                        <button key={tag} type="button" className="block w-full text-left px-4 py-2 text-gray-300 hover:bg-gray-700" onClick={() => selectTagSuggestion(tag)}>
                          #{tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>


              <div className="flex justify-end items-center">
                {errors.submit && <p className="text-red-500 text-sm mr-auto">{errors.submit}</p>}
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary disabled:bg-gray-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md"
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.97 }}
                >
                  {isSubmitting ? 'Creating...' : 'Create Post'}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CreatePost;